import {
  ActionIcon,
  Card,
  Center,
  createStyles,
  Group,
  LoadingOverlay,
  ScrollArea,
  Select,
  Table,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";

import { IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons";
import axios from "axios";
import { customLoader } from "components/utilities/loader";
import { Fragment, useEffect, useState } from "react";
import { API, formatNumber, PAGE_SIZE_OPTIONS } from "utiles/url";

const useStyles = createStyles((theme) => ({
  th: {
    padding: "0 !important",
  },

  control: {
    width: "100%",
    padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },

  icon: {
    width: 21,
    height: 21,
    borderRadius: 21,
  },
  thh: {
    color: "#666666",
    fontFamily: "'__Inter_aaf875','__Inter_Fallback_aaf875'",
    fontSize: "10px",
    textTransform: "uppercase",
    fontWeight: "bold",
  },
  idColumn: {
    width: "3%",
  },
  searchContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  searchInput: {
    flexGrow: 1,
    paddingRight: "50px", // Add padding to avoid text overlap with button
  },
  searchButton: {
    position: "absolute",
    right: 0,
    borderRadius: "0 4px 4px 0",
    height: "70%",
    width: "40px", // Fixed width for the button
    backgroundColor: "#FF6A00",
    color: "#FFFFFF",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "14px",
    cursor: "pointer",
  },
}));

function Th({ children, sortable, sorted, reversed, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  return (
    <th className={classes.th}>
      <UnstyledButton
        onClick={sortable ? onSort : null}
        className={classes.control}
      >
        <Group position="apart">
          <Text weight={500} size="sm">
            {children}
          </Text>
          {sortable && (
            <Center className={classes.icon}>
              <Icon size={14} stroke={1.5} />
            </Center>
          )}
        </Group>
      </UnstyledButton>
    </th>
  );
}

const MinmumStock = () => {
  const { classes } = useStyles();
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);

  const [sortedData, setSortedData] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [size, setSize] = useState("10");
  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
    fetchData(newSize);
  };
  useEffect(() => {
    fetchData(size);
  }, []);

  const fetchData = async (size) => {
    setLoading(true);
    try {
      let token = localStorage.getItem("auth_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.get(
        `${API}/stocks/list/below_minimum_stock_level?page=${activePage}&first=${size}`,
        config,
      );
      if (response.data) {
        setLoading(false);
        setDrivers(response.data.data);
        setSortedData(response.data.data); // Ensure sorting is applied when data is fetched
        setTotal(response.data?.links);
        setTotalPages(response.data.paginatorInfo.lastPage);
        setTotalRecords(response.data.paginatorInfo.total);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const sortData = (data, payload) => {
    if (!payload.sortBy) {
      return filterData(data, payload.search);
    }

    return filterData(
      [...data].sort((a, b) => {
        if (payload.reversed) {
          return b[payload.sortBy].localeCompare(a[payload.sortBy]);
        }

        return a[payload.sortBy].localeCompare(b[payload.sortBy]);
      }),
      payload.search,
    );
  };

  const handleChange = (page) => {
    setActivePage(page);
    fetchData(size);
  };

  const handleSort = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(drivers, { sortBy: field, reversed }));
  };

  const filterData = (data, search) => {
    const query = search.toLowerCase().trim();
    return data.filter((item) =>
      Object.keys(item).some(
        (key) =>
          typeof item[key] === "string" &&
          item[key] &&
          item[key].toLowerCase().includes(query),
      ),
    );
  };

  const theme = useMantineTheme();
  const rows = sortedData?.map((row) => (
    <Fragment key={row.id}>
      <tr>
        <td>{row.warehouse?.name}</td>
        <td>{row.product_sku?.sku}</td>
        <td>{row.quantity}</td>
        <td>{row.minimum_stock_level}</td>
      </tr>
    </Fragment>
  ));

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Card shadow="sm" p="lg">
        <ScrollArea>
          <Table
            highlightOnHover
            horizontalSpacing="md"
            verticalSpacing="xs"
            sx={{ tableLayout: "fixed", minWidth: 700 }}
          >
            <thead>
              <tr style={{ backgroundColor: "#F1F1F1" }}>
                <Th sortable={false} onSort={() => handleSort("name")}>
                  <span className={classes.thh}>Warehouse</span>
                </Th>
                <Th sortable={false} onSort={() => handleSort("email")}>
                  <span className={classes.thh}> Product Sku</span>
                </Th>
                <Th sortable={false} onSort={() => handleSort("quantity")}>
                  <span className={classes.thh}> Quantity </span>
                </Th>

                <Th
                  sortable={false}
                  onSort={() => handleSort("minimum_stock_level")}
                >
                  <span className={classes.thh}>Stock Level </span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {rows?.length > 0 ? (
                rows
              ) : (
                <tr>
                  <td colSpan={4}>
                    <Text weight={500} align="center">
                      Nothing found
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "12px",
              padding: "6px 4px",
              borderTop: "1px solid #e9ecef",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {/* Left: Total Records */}
            <Text size="sm" style={{ color: "#FF6A00" }}>
              {`Total Records: ${formatNumber(totalRecords)}`}
            </Text>

            {/* Right: Rows select + navigation */}
            <Group spacing={6} noWrap>
              <Text size="sm" color="dimmed">
                Rows
              </Text>
              <Select
                value={size}
                onChange={handlePageSizeChange}
                data={PAGE_SIZE_OPTIONS}
                style={{ width: 72 }}
                size="xs"
              />
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleChange(1)}
                disabled={activePage <= 1}
                title="First page"
                style={{ fontWeight: 700 }}
              >
                «
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleChange(activePage - 1)}
                disabled={activePage <= 1}
                title="Previous page"
              >
                ‹
              </ActionIcon>
              <Text size="sm" style={{ minWidth: 40, textAlign: "center" }}>
                {activePage} / {totalPages}
              </Text>
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleChange(activePage + 1)}
                disabled={activePage >= totalPages}
                title="Next page"
              >
                ›
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleChange(totalPages)}
                disabled={activePage >= totalPages}
                title="Last page"
                style={{ fontWeight: 700 }}
              >
                »
              </ActionIcon>
            </Group>
          </div>
        </ScrollArea>
      </Card>
    </div>
  );
};

export default MinmumStock;
